import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import getPriceLabel from '@dashboard/services/translation/getPriceLabel';
import useSubscriptionQueries from '@dashboard/hooks/queries/useSubscriptionQueries';
import { format } from 'date-fns';
import StatisticsList from './StatisticsList';
import { recentSubscriptionsKey } from '@dashboard/lib/queryKeys';

function useRecentSubscriptions() {
  const { sellerId } = useAuth();

  const { getSubscriptions } = useSubscriptionQueries();

  const { data, isError, isLoading } = useQuery({
    queryKey: recentSubscriptionsKey(sellerId),
    queryFn: () =>
      getSubscriptions(sellerId, {
        limit: 4,
        livemode: true,
        status: ['active', 'trialing', 'scheduled'],
      }),
    staleTime: 1000 * 60, // 5 minutes
    refetchOnMount: true,
    refetchOnWindowFocus: true,
  });

  return {
    isLoading,
    isError,
    data,
  };
}

export default function RecentSubscriptions() {
  const { data, isLoading, isError } = useRecentSubscriptions();

  return (
    <StatisticsList.List>
      {isLoading && <StatisticsList.Loading />}

      {isError && <StatisticsList.EmptyState>Something went wrong</StatisticsList.EmptyState>}

      {!isLoading && !data?.length && (
        <StatisticsList.EmptyState>No subscriptions yet</StatisticsList.EmptyState>
      )}

      {data?.map(subscription => (
        <StatisticsList.Item key={subscription._id} href={`/subscriptions/${subscription._id}`}>
          <StatisticsList.Left>
            <StatisticsList.PrimaryText>
              {subscription.customer?.email || subscription.customerEmail}
            </StatisticsList.PrimaryText>
            <StatisticsList.SecondaryText>
              {subscription.product.title}
            </StatisticsList.SecondaryText>
          </StatisticsList.Left>
          <StatisticsList.Right>
            <StatisticsList.PrimaryText>
              {getPriceLabel({
                amount: subscription.amount,
                currency: subscription.currency,
              })}
            </StatisticsList.PrimaryText>

            <StatisticsList.SecondaryText>
              {format(new Date(subscription.createdAt), 'MMM d, yyyy HH:mm')}
            </StatisticsList.SecondaryText>
          </StatisticsList.Right>
        </StatisticsList.Item>
      ))}
    </StatisticsList.List>
  );
}
